'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useRef } from 'react';
import * as Popover from '@radix-ui/react-popover';

/**
 * Tooltip using Radix Popover with hover support
 *
 * mobileMode="toggle" (default):
 *   - Desktop: hover to show, click to toggle
 *   - Mobile: tap to toggle, tap outside to dismiss
 *   - Click prevents parent link navigation
 *
 * mobileMode="disabled":
 *   - Desktop: hover only, click passes through to action
 *   - Mobile: no tooltip, click passes through to action
 *   - For action icons like favorites, review stars
 */
const Tooltip = t0 => {
  const $ = _c(36);
  const {
    content,
    children,
    side: t1,
    mobileMode: t2,
    onOpenChange
  } = t0;
  const side = t1 === undefined ? "top" : t1;
  const mobileMode = t2 === undefined ? "toggle" : t2;
  const [open, setOpenState] = useState(false);
  let t3;
  if ($[0] !== onOpenChange) {
    t3 = newOpen => {
      setOpenState(newOpen);
      onOpenChange?.(newOpen);
    };
    $[0] = onOpenChange;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const setOpen = t3;
  const isHoveringRef = useRef(false);
  const isTouchRef = useRef(false);
  const clickLockedRef = useRef(false);
  const hoverTimeoutRef = useRef(null);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      isTouchRef.current = true;
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const handleTouchStart = t4;
  let t5;
  if ($[3] !== setOpen) {
    t5 = () => {
      if (isTouchRef.current) {
        return;
      }
      isHoveringRef.current = true;
      clearTimeout(hoverTimeoutRef.current);
      if (clickLockedRef.current) {
        return;
      }
      hoverTimeoutRef.current = setTimeout(() => {
        setOpen(true);
      }, 100);
    };
    $[3] = setOpen;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleMouseEnter = t5;
  let t6;
  if ($[5] !== setOpen) {
    t6 = () => {
      if (isTouchRef.current) {
        isTouchRef.current = false;
        return;
      }
      isHoveringRef.current = false;
      clickLockedRef.current = false;
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        setOpen(false);
      }, 100);
    };
    $[5] = setOpen;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const handleMouseLeave = t6;
  let t7;
  if ($[7] !== setOpen) {
    t7 = () => {
      if (!isTouchRef.current) {
        setOpen(true);
      }
    };
    $[7] = setOpen;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const handleFocus = t7;
  let t8;
  if ($[9] !== setOpen) {
    t8 = () => {
      if (!isHoveringRef.current) {
        setOpen(false);
      }
    };
    $[9] = setOpen;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const handleBlur = t8;
  let t9;
  if ($[11] !== mobileMode || $[12] !== open || $[13] !== setOpen) {
    t9 = e => {
      if (mobileMode === "disabled") {
        if (open) {
          setOpen(false);
        }
        return;
      }
      e.preventDefault();
      if (open) {
        setOpen(false);
        if (isHoveringRef.current) {
          clickLockedRef.current = true;
        }
      } else {
        setOpen(true);
        clickLockedRef.current = false;
      }
    };
    $[11] = mobileMode;
    $[12] = open;
    $[13] = setOpen;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const handleClick = t9;
  let t10;
  if ($[15] !== handleMouseEnter || $[16] !== handleMouseLeave || $[17] !== mobileMode) {
    t10 = mobileMode === "toggle" ? {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave
    } : {};
    $[15] = handleMouseEnter;
    $[16] = handleMouseLeave;
    $[17] = mobileMode;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const contentHoverHandlers = t10;
  let t11;
  if ($[19] !== children || $[20] !== handleBlur || $[21] !== handleClick || $[22] !== handleFocus || $[23] !== handleMouseEnter || $[24] !== handleMouseLeave) {
    t11 = <Popover.Trigger asChild={true}><span onTouchStart={handleTouchStart} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} onFocus={handleFocus} onBlur={handleBlur} onClick={handleClick}>{children}</span></Popover.Trigger>;
    $[19] = children;
    $[20] = handleBlur;
    $[21] = handleClick;
    $[22] = handleFocus;
    $[23] = handleMouseEnter;
    $[24] = handleMouseLeave;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <Popover.Arrow className="fill-gray-3" />;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== content || $[28] !== contentHoverHandlers || $[29] !== side) {
    t13 = <Popover.Portal><Popover.Content side={side} sideOffset={8} collisionPadding={16} {...contentHoverHandlers} onOpenAutoFocus={_temp} className="bg-gray-3 text-light text-xs px-3 py-2 rounded shadow max-w-64 z-[9999]">{content}{t12}</Popover.Content></Popover.Portal>;
    $[27] = content;
    $[28] = contentHoverHandlers;
    $[29] = side;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== open || $[32] !== setOpen || $[33] !== t11 || $[34] !== t13) {
    t14 = <Popover.Root open={open} onOpenChange={setOpen}>{t11}{t13}</Popover.Root>;
    $[31] = open;
    $[32] = setOpen;
    $[33] = t11;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  return t14;
};
export default Tooltip;
function _temp(e_0) {
  return e_0.preventDefault();
}